/**
 * Real-Time Signals API Client
 * 
 * API methods for generating, saving, and retrieving real-time signals.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate signal suggestions using the 3-step agent chain.
 * 
 * @param {Object} params
 * @param {string} params.assetId
 * @param {string} params.confirmedPlanId
 * @param {Object} params.step0GoalOffer
 * @param {Object} params.step1Persona
 * @param {Array} params.step2Segments
 * @param {Object} params.assetBundle
 * @returns {Promise<{success: boolean, signals: Array, metadata: Object}>}
 */
export async function suggestSignals({
  assetId,
  confirmedPlanId,
  step0GoalOffer,
  step1Persona,
  step2Segments,
  assetBundle
}) {
  // Build request body ensuring null values are converted to empty objects/arrays
  const requestBody = {
    asset_id: assetId || '',
    confirmed_plan_id: confirmedPlanId || '',
    step0_goal_offer: step0GoalOffer || {},
    step1_persona: step1Persona || {},
    step2_segments: Array.isArray(step2Segments) ? step2Segments : [],
    asset_bundle: assetBundle || {}
  };
  
  console.log('[SignalsAPI] Sending request:', JSON.stringify(requestBody, null, 2));
  
  const response = await fetch(`${API_BASE}/api/realtime-signals/suggest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to generate signals');
  }

  return response.json();
}

/**
 * Save selected signals and link them to a confirmed plan.
 * 
 * @param {Object} params
 * @param {string} params.confirmedPlanId
 * @param {Array<string>} params.confirmedCampaignIds
 * @param {Array} params.selectedSignals
 * @returns {Promise<{success: boolean, saved_count: number, link_count: number}>}
 */
export async function saveSignals({
  confirmedPlanId,
  confirmedCampaignIds = [],
  selectedSignals
}) {
  const response = await fetch(`${API_BASE}/api/realtime-signals/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      confirmed_plan_id: confirmedPlanId,
      confirmed_campaign_ids: confirmedCampaignIds,
      selected_signals: selectedSignals
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to save signals');
  }

  return response.json();
}

/**
 * Get saved signals for a confirmed plan.
 * 
 * @param {string} confirmedPlanId
 * @returns {Promise<{success: boolean, signals: Array}>}
 */
export async function getSignalsForPlan(confirmedPlanId) {
  const response = await fetch(
    `${API_BASE}/api/realtime-signals?confirmed_plan_id=${encodeURIComponent(confirmedPlanId)}`,
    {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to get signals');
  }

  return response.json();
}

/**
 * Get all signals for an asset.
 * 
 * @param {string} assetId
 * @returns {Promise<{success: boolean, signals: Array}>}
 */
export async function getSignalsForAsset(assetId) {
  const response = await fetch(
    `${API_BASE}/api/realtime-signals?asset_id=${encodeURIComponent(assetId)}`,
    {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to get signals');
  }

  return response.json();
}

/**
 * Delete a signal.
 * 
 * @param {string} signalId
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteSignal(signalId) {
  const response = await fetch(
    `${API_BASE}/api/realtime-signals/${encodeURIComponent(signalId)}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to delete signal');
  }

  return response.json();
}

// Signal categories with display info
export const SIGNAL_CATEGORIES = {
  regulatory: { label: 'Regulatory', color: 'bg-red-100 text-red-700 border-red-200' },
  seasonal: { label: 'Seasonal', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  competitor: { label: 'Competitor', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  market: { label: 'Market', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  platform: { label: 'Platform', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  customer_intent: { label: 'Customer Intent', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  company_events: { label: 'Company Events', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  content_trends: { label: 'Content Trends', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  other: { label: 'Other', color: 'bg-slate-100 text-slate-700 border-slate-200' }
};
